/**
 * Advanced markdown link parser that classifies links by type and resolves paths
 * Used for the new link resolver system that converts note links to wiki links
 */

export type LinkKind = 'note' | 'attachment' | 'external'

/**
 * Resolves a relative path against a base directory path
 * @param baseDirectory - The base directory path (e.g., "folder/subfolder")
 * @param relativePath - The relative path to resolve (e.g., "../image.png")
 * @returns The resolved absolute path within the import batch
 */
export function resolveRelativePath(baseDirectory: string, relativePath: string): string {
  // Handle absolute paths (already resolved)
  if (relativePath.startsWith('/')) {
    return relativePath.slice(1) // Remove leading slash for consistency
  }

  // Split paths into components
  const baseParts = baseDirectory ? baseDirectory.split('/').filter(Boolean) : []
  const relativeParts = relativePath.split('/').filter(Boolean)

  // Start with base directory components
  const resolvedParts = [...baseParts]

  // Process each component of the relative path
  for (const part of relativeParts) {
    if (part === '..') {
      // Go up one directory
      resolvedParts.pop()
    } else if (part !== '.') {
      // Add the component (skip current directory references)
      resolvedParts.push(part)
    }
  }

  return resolvedParts.join('/')
}

export type MarkdownLink = {
  raw: string        // [Level 1](../level1.md)
  text: string       // Level 1
  target: string     // ../level1.md
  resolved: string   // level1/level1-note.md
  kind: LinkKind
  isImage: boolean
}

/**
 * Determines if a file path represents a markdown note based on its extension
 */
function isMarkdownFile(path: string): boolean {
  const markdownExtensions = ['.md', '.markdown', '.mdown', '.mkd', '.mkdn']
  const lowerPath = path.toLowerCase()
  return markdownExtensions.some(extension => lowerPath.endsWith(extension))
}

/**
 * Classifies a link target to determine its type
 */
function classifyLink(target: string): LinkKind {
  const t = target.trim()
  const lower = t.toLowerCase()

  // Treat placeholder "url" as external
  if (lower === 'url') {
    return 'external'
  }

  // External links (HTTP/HTTPS/MAILTO)
  if (
    lower.startsWith('http://') ||
    lower.startsWith('https://') ||
    lower.startsWith('mailto:')
  ) {
    return 'external'
  }
  
  // Anchor links are considered external (not processed)
  if (t.startsWith('#')) {
    return 'external'
  }
  
  // Check if it's a markdown file (note link)
  if (isMarkdownFile(t)) {
    return 'note'
  }
  
  // Everything else is an attachment
  return 'attachment'
}

/**
 * Parses all markdown links from content and classifies them by type
 * @param content - The markdown content to parse
 * @param notePath - The full path of the note (e.g., "folder/subfolder/note.md")
 * @returns Array of parsed and classified markdown links
 */
export function parseMarkdownLinks(
  content: string,
  notePath: string
): MarkdownLink[] {
  const links: MarkdownLink[] = []
  const lastSlashIndex = notePath.lastIndexOf('/')
  const noteDirectory = lastSlashIndex === -1 ? '' : notePath.slice(0, lastSlashIndex)

  // Match image and link patterns with support for parentheses in URLs and optional titles
  // Examples: [text](path), ![alt](path), [text](path "title"), [text](Image (2).png)
  // Pattern explanation:
  // - (?:[^"()]|\([^()]*\))+? matches URL allowing balanced parens, stops before optional title
  // - (?:\s+["'][^"']*["'])? optionally matches space + quoted title after URL
  const linkRegex = /(!?)\[([^\]]*)]\(((?:[^"()]|\([^()]*\))+?)(?:\s+["'][^"']*["'])?\s*\)/g
  const matches = content.matchAll(linkRegex)

  for (const match of matches) {
    const isImage = match[1] === '!'
    const text = match[2]
    let target = match[3].trim()
    const raw = match[0]
    
    // Strip angle brackets from Craft-style markdown links
    // Craft wraps paths with spaces/parentheses in angle brackets: <path/to/file (1).png>
    // These are markdown delimiters, not part of the actual path
    if (target.startsWith('<') && target.endsWith('>')) {
      target = target.slice(1, -1).trim()
    }

    // Classify the link type first
    const kind = classifyLink(target)

    // For local files, strip query/hash before resolution
    if (kind !== 'external') {
      target = target.split('#')[0].split('?')[0]
    }

    // Only resolve relative paths for local files (note and attachment links)
    let resolved = (kind === 'external')
      ? target
      : resolveRelativePath(noteDirectory, target)
    
    // Decode URL-encoded paths (Craft exports use encoded paths in markdown)
    // e.g., "Craft%20Handbuch%20%F0%9F%93%96.assets/..." → "Craft Handbuch 📖.assets/..."
    if (kind === 'attachment' || kind === 'note') {
      try {
        resolved = decodeURIComponent(resolved)
      } catch {
        // Keep original if decoding fails (not URL-encoded)
      }
    }

    links.push({
      raw,
      text,
      target,
      resolved,
      kind,
      isImage
    })
  }

  return links
}
